/* eslint-disable no-console -- This module is the single sanctioned console boundary. */
import { config } from '@/app/config/env';

/**
 * Application logger.
 *
 * Two responsibilities beyond wrapping `console`:
 *
 * 1. Verbosity is environment-aware — debug/info output is dropped in
 *    production so the browser console stays clean for real problems.
 * 2. Every payload is redacted before it is written. Tokens, passwords, and
 *    similar fields routinely ride along inside error objects and API
 *    responses; logging them would persist credentials into browser consoles,
 *    session-replay tools, and support screenshots.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

/** Field names whose values are replaced with a redaction marker. */
const SENSITIVE_KEY_PATTERN =
  /(pass(word|phrase)?|secret|token|auth|credential|apikey|api_key|session|cookie|jwt|bearer|otp|pin|ssn|creditcard|card_number|cvv)/i;

const REDACTED = '[redacted]';
const MAX_DEPTH = 6;

function redact(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) return '[max depth]';
  if (value === null || typeof value !== 'object') return value;

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      // Stacks are kept only outside production; they reveal internal paths
      // and module structure that need not reach a user's console.
      ...(config.isProduction ? {} : { stack: value.stack }),
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1));
  }

  const output: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    output[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redact(entry, depth + 1);
  }
  return output;
}

const minimumLevel: LogLevel = config.isProduction ? 'warn' : 'debug';

function shouldLog(level: LogLevel): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[minimumLevel];
}

function write(level: LogLevel, message: string, context?: unknown): void {
  if (!shouldLog(level)) return;

  const payload = context === undefined ? [] : [redact(context)];

  switch (level) {
    case 'debug':
    case 'info':
      console.log(`[${level}] ${message}`, ...payload);
      break;
    case 'warn':
      console.warn(`[warn] ${message}`, ...payload);
      break;
    case 'error':
      console.error(`[error] ${message}`, ...payload);
      break;
  }
}

export const logger = {
  debug: (message: string, context?: unknown) => {
    write('debug', message, context);
  },
  info: (message: string, context?: unknown) => {
    write('info', message, context);
  },
  warn: (message: string, context?: unknown) => {
    write('warn', message, context);
  },
  error: (message: string, context?: unknown) => {
    write('error', message, context);
  },
};

/** Exported for unit tests covering the redaction rules. */
export const __testing = { redact };
